/* eslint-disable radix */
/* eslint-disable consistent-return */
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';
import Loader from '../components/Loader';
import V1 from '../pages/V1';
import V2 from '../pages/V2';
import V3 from '../pages/V3';
import V4 from '../pages/V4';
import V5 from '../pages/V5';
import ModelService from '../services/ModelService';
import { useStore } from '../stores';

export default function ProtectedRoute({
  loggedComponent,
  defaultComponent,
  isModel = false,
  isClient = false,
  requireVerification = false,
  isUnique = false,
}) {
  const [loading, setLoading] = useState(true);

  const {
    user,
    login,
    logout,
    isVerified,
    modelVerification,
    setModelVerification,
  } = useStore(
    useShallow((state) => ({
      user: state.user,
      login: state.login,
      logout: state.logout,
      isVerified: state.isVerified,
      modelVerification: state.modelVerification,
      setModelVerification: state.setModelVerification,
    }))
  );
  const navigate = useNavigate();

  const KEY = useMemo(() => process.env.REACT_APP_LOCAL_STORAGE_USER_KEY, []);

  useEffect(() => {
    try {
      setLoading(true);

      const alreadyStored = localStorage.getItem(KEY);
      if (!alreadyStored) {
        setLoading(false);
        return;
      }

      const userObj = JSON.parse(alreadyStored);

      login(userObj);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [KEY, login, logout]);

  useEffect(() => {
    async function loadMeta() {
      try {
        setLoading(true);

        if (user?.type === 'model') {
          const findModel = await ModelService.findDateAndVerification(user.id);

          setModelVerification(findModel);
        } else {
          setModelVerification(null);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }

    if (user) {
      loadMeta();
    }
  }, [user, setModelVerification]);

  useEffect(() => {
    if (!requireVerification && user && isModel && modelVerification) {
      let caminhoRedirecionamento = null;

      if (
        !modelVerification.dateOfBirth ||
        modelVerification.dateOfBirth === null
      ) {
        caminhoRedirecionamento = '/modelrequired#edit';
      } else if (
        !modelVerification.modelVerification ||
        modelVerification.modelVerification === null
      ) {
        caminhoRedirecionamento = '/modeluploads#edit';
      }

      if (caminhoRedirecionamento) {
        navigate(caminhoRedirecionamento);
      }
    }
  }, [requireVerification, user, isModel, modelVerification, navigate]);

  if (loading) return <Loader isLoading={loading} />;

  if (requireVerification && isVerified) {
    switch (isVerified) {
      case 1:
        return <V1 />;
      case 2:
        return <V2 />;
      case 3:
        return <V3 />;
      case 4:
        return <V4 />;
      case 5:
        return <V5 />;
      default:
        return defaultComponent;
    }
  }

  if (!user) {
    return defaultComponent;
  }

  if (modelVerification?.dateOfBirth !== null && isUnique) {
    return defaultComponent;
  }

  if (isModel && user?.type?.toUpperCase() !== 'MODEL') {
    return defaultComponent;
  }

  if (isClient && user?.type?.toUpperCase() !== 'CLIENT') {
    return defaultComponent;
  }

  return loggedComponent;
}

ProtectedRoute.propTypes = {
  loggedComponent: PropTypes.node.isRequired,
  defaultComponent: PropTypes.node.isRequired,
  isModel: PropTypes.bool,
  isClient: PropTypes.bool,
  requireVerification: PropTypes.bool,
  isUnique: PropTypes.bool,
};
